import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { useSearch } from "../../context/SearchContext";
import { estimateTrip, getSearchResultsConfig } from "../../api/vehicleApi";
import { reverseGeocodeVehicleLocation } from "../../api/ownerCarsApi";
import LocationAutoInput from "./LocationAutoInput";
import Icon from "@mdi/react";
import {
  mdiMapMarkerPlus,
  mdiMapMarkerOutline,
  mdiFlagOutline,
  mdiCrosshairsGps,
  mdiTrashCanOutline,
  mdiClockOutline,
  mdiCalendarMonthOutline,
  mdiSync,
  mdiArrowRight,
  mdiInformationOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiRoutes,
  mdiTapeMeasure,
  mdiPlus,
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
} from "@mdi/js";

/* -------------------- date/time helpers -------------------- */
const STEP_MIN = 15;
const pad = (n) => String(n).padStart(2, "0");
const toYmd = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function roundUpToStep(date, stepMin = 15) {
  const ms = stepMin * 60 * 1000;
  const rounded = new Date(Math.ceil(date.getTime() / ms) * ms);
  return `${pad(rounded.getHours())}:${pad(rounded.getMinutes())}`;
}
function ceilToStepDate(date, stepMin = 15) {
  const ms = stepMin * 60 * 1000;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
}
function fmtTime24(d) {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function toLocalDateTime(dateStr, time24) {
  if (!dateStr || !time24) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = time24.split(":").map(Number);
  const dt = new Date();
  dt.setFullYear(y, m - 1, d);
  dt.setHours(hh, mm || 0, 0, 0);
  return dt;
}

/* 12h helpers reused by picker & summary */
function to12PartsFrom24(hhmm) {
  if (!hhmm) return { hour12: 12, minute: 0, ampm: "AM" };
  const [hh, mm] = hhmm.split(":").map(Number);
  const ampm = hh < 12 ? "AM" : "PM";
  const hour12 = hh % 12 === 0 ? 12 : hh % 12;
  return { hour12, minute: mm, ampm };
}
function partsTo24(hour12, minute, ampm) {
  let h24 = hour12 % 12;
  if (ampm === "PM") h24 += 12;
  if (ampm === "AM" && hour12 === 12) h24 = 0;
  return `${pad(h24)}:${pad(minute)}`;
}
function to12From24(hhmm) {
  if (!hhmm) return "";
  const { hour12, minute, ampm } = to12PartsFrom24(hhmm);
  return `${hour12}:${pad(minute)} ${ampm}`;
}

function formatLeadWindow(minutes) {
  const safe = Math.max(0, Math.floor(Number(minutes) || 0));
  if (safe >= 1440 && safe % 1440 === 0) {
    const days = safe / 1440;
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  if (safe >= 60 && safe % 60 === 0) {
    const hours = safe / 60;
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  if (safe >= 60) {
    const hours = Math.floor(safe / 60);
    const mins = safe % 60;
    return `${hours} hr ${mins} min`;
  }
  return `${safe} min`;
}
/* ---------------------------------------------------------- */

function Section({ title, hint, children, mobileMode = false }) {
  return (
    <div
      className={
        mobileMode
          ? "border-0 bg-transparent p-0"
          : "rounded-2xl border border-slate-200 bg-white/70 p-5"
      }
    >
      {(title || (!mobileMode && hint)) && (
        <div className="mb-3">
          {title ? (
            <div className={`${mobileMode ? "text-[16px]" : "text-sm"} font-semibold text-slate-900`}>
              {title}
            </div>
          ) : null}
          {!mobileMode && hint && (
            <div className={`${mobileMode ? "text-sm" : "text-xs"} mt-0.5 text-slate-500`}>
              {hint}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

/* ---------------- 12-hour time picker (web, app-style) ---------------- */
function TimePicker12({
  valueHHmm,
  onChangeHHmm,
  minHHmm,
  disabled,
  mobileMode = false,
  leadingIcon = null,
  onBlockedAttempt = null,
  blockedLabel = "Select time",
}) {
  const [open, setOpen] = useState(false);

  const init = useMemo(
    () => to12PartsFrom24(valueHHmm || minHHmm || "12:00"),
    [valueHHmm, minHHmm]
  );
  const [h12, setH12] = useState(init.hour12);
  const [minute, setMinute] = useState(
    Math.round(init.minute / STEP_MIN) * STEP_MIN
  );
  const [ampm, setAmpm] = useState(init.ampm);

  useEffect(() => {
    const p = to12PartsFrom24(valueHHmm || minHHmm || "12:00");
    setH12(p.hour12);
    setMinute(Math.round(p.minute / STEP_MIN) * STEP_MIN);
    setAmpm(p.ampm);
  }, [valueHHmm, minHHmm]);

  const composeAndValidate = (h, m, ap) => {
    let candidate = partsTo24(h, m, ap);
    if (minHHmm && candidate < minHHmm) {
      candidate = minHHmm;
      const p = to12PartsFrom24(candidate);
      setH12(p.hour12);
      setMinute(p.minute);
      setAmpm(p.ampm);
      onChangeHHmm(candidate);
      return false;
    }
    onChangeHHmm(candidate);
    return true;
  };

  const disabledMinute = (m) => {
    if (!minHHmm) return false;
    const check = partsTo24(h12, m, ampm);
    return check < minHHmm;
  };
  const disabledHour = (h) => {
    if (!minHHmm) return false;
    const minsToCheck = [minute, 0, 15, 30, 45];
    for (const m of minsToCheck) {
      if (partsTo24(h, m, ampm) >= (minHHmm || "00:00")) return false;
    }
    return true;
  };
  const disabledAmPm = (ap) => {
    if (!minHHmm) return false;
    const combos = [
      [h12, minute],
      [h12, 0],
      [h12, 15],
      [h12, 30],
      [h12, 45],
      [1, 0],
      [12, 0],
    ];
    return !combos.some(
      ([h, m]) => partsTo24(h, m, ap) >= (minHHmm || "00:00")
    );
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const triggerLabel = valueHHmm
    ? to12From24(valueHHmm)
    : blockedLabel;
  const isBlocked = disabled && typeof onBlockedAttempt === "function";

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (isBlocked) {
            onBlockedAttempt();
            return;
          }
          if (!disabled) setOpen(true);
        }}
        className={`mt-1 relative w-full rounded-xl border border-slate-300 text-left focus:outline-none focus:ring-2 focus:ring-slate-300 ${
          mobileMode ? "px-4 py-4 text-[14px]" : "px-3 py-2"
        } ${
          disabled
            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
            : "bg-white text-slate-900"
        }`}
        aria-disabled={disabled}
      >
        {leadingIcon ? (
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
            {leadingIcon}
          </span>
        ) : null}
        <span className={leadingIcon ? "block pl-8" : "block"}>{triggerLabel}</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center">
          <div
            className="absolute inset-0"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          <div className="relative z-50 w-full max-w-md rounded-2xl bg-white shadow-xl sm:mb-0 mb-4 mx-4">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div className="text-sm font-semibold text-slate-900">
                Select time
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs font-medium text-slate-500 hover:text-slate-900"
              >
                Close
              </button>
            </div>

            <div className="px-4 pt-3 pb-4 space-y-3">
              <div className="flex gap-2">
                {["AM", "PM"].map((k) => {
                  const selected = ampm === k;
                  const isDisabled = disabledAmPm(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => {
                        if (isDisabled) return;
                        const next = k;
                        setAmpm(next);
                        composeAndValidate(h12, minute, next);
                      }}
                      className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold ${
                        selected
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-300 bg-white text-slate-700"
                      } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {k}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                    Hour
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
                      const selected = h12 === h;
                      const isDisabled = disabledHour(h);
                      return (
                        <button
                          key={h}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => {
                            if (isDisabled) return;
                            setH12(h);
                            composeAndValidate(h, minute, ampm);
                          }}
                          className={`flex w-full items-center justify-between px-3 py-2 text-sm ${
                            selected ? "bg-slate-100" : "bg-white"
                          } ${
                            isDisabled ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        >
                          <span className="text-slate-900">{h}</span>
                          {selected && (
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                    Minute
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {[0, 15, 30, 45].map((m) => {
                      const selected = minute === m;
                      const isDisabled = disabledMinute(m);
                      return (
                        <button
                          key={m}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => {
                            if (isDisabled) return;
                            setMinute(m);
                            const ok = composeAndValidate(h12, m, ampm);
                            if (ok) setOpen(false);
                          }}
                          className={`flex w-full items-center justify-between px-3 py-2 text-sm ${
                            selected ? "bg-slate-100" : "bg-white"
                          } ${
                            isDisabled ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        >
                          <span className="text-slate-900">{pad(m)}</span>
                          {selected && (
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {minHHmm && (
                <div className="mt-1 text-[11px] text-slate-500">
                  Earliest: {to12From24(minHHmm)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- main form ---------------- */
export default function TripDetailsForm({
  mobileMode = false,
  mobileFooterActions = null,
}) {
  const { payload, setPayload, categories, types, pushToResults } = useSearch();

  const [loadingDist, setLoadingDist] = useState(false);
  const [showStops, setShowStops] = useState(false);
  const [locatingField, setLocatingField] = useState("");
  const [locationErrorMessage, setLocationErrorMessage] = useState("");
  const [scheduleRequestThresholdMinutes, setScheduleRequestThresholdMinutes] =
    useState(null);
  const [scheduleConfigReady, setScheduleConfigReady] = useState(false);

  const debTimerRef = useRef(null);
  const lastComputedSigRef = useRef("");
  const inflightRef = useRef(null);

  const deriveCategoryValue = (list) => {
    const clean = Array.isArray(list)
      ? list.map((item) => String(item || "").trim()).filter(Boolean)
      : [];
    if (clean.length > 1) return "all";
    return clean[0] ?? null;
  };

  const injectSelections = useCallback(
    (next) => ({
      ...next,
      categories,
      types,
      category: deriveCategoryValue(categories),
      carType: types[0] ?? null,
      carTypes: types,
    }),
    [categories, types]
  );

  useEffect(() => {
    let alive = true;
    getSearchResultsConfig()
      .then((config) => {
        if (!alive) return;
        const nextThreshold = Number(config?.scheduleRequestThresholdMinutes);
        setScheduleRequestThresholdMinutes(
          Number.isFinite(nextThreshold) ? Math.max(0, nextThreshold) : 30
        );
        setScheduleConfigReady(true);
      })
      .catch(() => {
        if (!alive) return;
        setScheduleRequestThresholdMinutes(30);
        setScheduleConfigReady(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const ensureStopsResolved = (arr, len) => {
    const base = Array.isArray(arr) ? [...arr] : [];
    while (base.length < len) base.push("");
    if (base.length > len) base.length = len;
    return base;
  };

const clearEstimates = useCallback(() => {
  lastComputedSigRef.current = "";
  setPayload((p) =>
    injectSelections({
      ...p,
      totalDistance: null,
      durationHours: null,
      minDropOffsetMin: null,

      // ✅ also clear coords so /within-radius doesn't use stale values
      pickupLat: null,
      pickupLng: null,
      dropLat: null,
      dropLng: null,
    })
  );
}, [injectSelections, setPayload]);


  useEffect(() => {
    if (payload.roundTrip) {
      setPayload((p) =>
        injectSelections({
          ...p,
          finalDestination: p.pickupLocation || "",
          finalResolved: p.pickupResolved || "",
        })
      );
      setShowStops(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload.roundTrip, payload.pickupLocation, payload.pickupResolved]);

  const setPickupTyped = (val) => {
    clearEstimates();
    setPayload((p) =>
      injectSelections({
        ...p,
        pickupLocation: val,
        pickupResolved: "",
        ...(p.roundTrip ? { finalResolved: "" } : {}),
      })
    );
  };

  const setPickupSelected = (val) => {
    clearEstimates();
    setPayload((p) =>
      injectSelections({
        ...p,
        pickupLocation: val,
        pickupResolved: val,
        ...(p.roundTrip ? { finalDestination: val, finalResolved: val } : {}),
      })
    );
  };

  const setFinalTyped = (val) => {
    clearEstimates();
    setPayload((p) =>
      injectSelections({ ...p, finalDestination: val, finalResolved: "" })
    );
  };

  const setFinalSelected = (val) => {
    clearEstimates();
    setPayload((p) =>
      injectSelections({ ...p, finalDestination: val, finalResolved: val })
    );
  };

  const addStop = () => {
    clearEstimates();
    setPayload((p) => {
      const nextDest = [...(p.destinations || []), ""];
      const nextRes = ensureStopsResolved(p.stopsResolved, nextDest.length);
      return injectSelections({
        ...p,
        destinations: nextDest,
        stopsResolved: nextRes,
      });
    });
  };

  const updateStopTyped = (idx, val) => {
    clearEstimates();
    setPayload((p) => {
      const nextDest = [...(p.destinations || [])];
      nextDest[idx] = val;
      const nextRes = ensureStopsResolved(p.stopsResolved, nextDest.length);
      nextRes[idx] = "";
      return injectSelections({
        ...p,
        destinations: nextDest,
        stopsResolved: nextRes,
      });
    });
  };

  const updateStopSelected = (idx, val) => {
    clearEstimates();
    setPayload((p) => {
      const nextDest = [...(p.destinations || [])];
      nextDest[idx] = val;
      const nextRes = ensureStopsResolved(p.stopsResolved, nextDest.length);
      nextRes[idx] = val;
      return injectSelections({
        ...p,
        destinations: nextDest,
        stopsResolved: nextRes,
      });
    });
  };

  const removeStop = (idx) => {
    clearEstimates();
    setPayload((p) => {
      const nextDest = [...(p.destinations || [])];
      nextDest.splice(idx, 1);
      let nextRes = Array.isArray(p.stopsResolved) ? [...p.stopsResolved] : [];
      nextRes.splice(idx, 1);
      nextRes = ensureStopsResolved(nextRes, nextDest.length);
      return injectSelections({
        ...p,
        destinations: nextDest,
        stopsResolved: nextRes,
      });
    });
  };

  const setTripType = (isRound) => {
    clearEstimates();
    setPayload((p) =>
      injectSelections({
        ...p,
        roundTrip: Boolean(isRound),
        finalDestination: isRound
          ? p.pickupLocation || p.finalDestination || ""
          : p.finalDestination,
        finalResolved: isRound ? p.pickupResolved || "" : p.finalResolved || "",
      })
    );
  };

  const applyCurrentLocationToField = useCallback(
    (field, idx = null) => {
      if (typeof window === "undefined" || !navigator?.geolocation) {
        setLocationErrorMessage("Current location is not available in this browser.");
        return;
      }
      const key = idx == null ? field : `${field}-${idx}`;
      setLocatingField(key);
      navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
          try {
            const data = await reverseGeocodeVehicleLocation(
              coords.latitude,
              coords.longitude
            );
            const address = String(
              data?.formattedAddress ||
                data?.displayName ||
                data?.address ||
                data?.fullText ||
                data?.name ||
                ""
            ).trim();
            if (!address) throw new Error("Unable to resolve current location");
            if (field === "pickup") {
              setPickupSelected(address);
            } else if (field === "destination") {
              setFinalSelected(address);
            } else if (field === "stop" && idx != null) {
              updateStopSelected(idx, address);
            }
          } catch (err) {
            console.error("[search][web] current location resolve failed", err);
            setLocationErrorMessage(
              "We couldn't read your current location. Please try again or type the address manually."
            );
          } finally {
            setLocatingField("");
          }
        },
        (err) => {
          console.error("[search][web] current location unavailable", err);
          const needsOverlayHelp =
            err?.code === 1 ||
            String(err?.message || "").toLowerCase().includes("permission");
          setLocationErrorMessage(
            needsOverlayHelp
              ? "Current location couldn't be used. Close any browser bubbles or phone overlays, allow location access, then try again."
              : "Current location couldn't be used right now. Please try again or type the address manually."
          );
          setLocatingField("");
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 15000,
        }
      );
    },
    [setPickupSelected, setFinalSelected]
  );

  /* -------------------- AUTO ESTIMATE (backend source-of-truth) -------------------- */
  const routeSignature = useMemo(() => {
    const stopsRes = ensureStopsResolved(
      payload.stopsResolved,
      payload.destinations?.length || 0
    )
      .map((s) => (s || "").trim())
      .filter(Boolean);

    const out = [payload.pickupResolved || "", ...stopsRes];
    if (payload.roundTrip) out.push(payload.pickupResolved || "");
    else if (payload.finalResolved) out.push(payload.finalResolved);

    return out.filter(Boolean).join(" | ");
  }, [
    payload.pickupResolved,
    payload.finalResolved,
    payload.stopsResolved,
    payload.destinations,
    payload.roundTrip,
  ]);

  const dropTimingKeySig = useMemo(
    () =>
      [
        routeSignature,
        payload.pickupDate || "",
        payload.pickupTime24 || "",
      ].join("|"),
    [payload.pickupDate, payload.pickupTime24, routeSignature]
  );

  const hasEnoughResolvedPoints = useMemo(() => {
    if (!payload.roundTrip) {
      return Boolean(payload.pickupResolved) && Boolean(payload.finalResolved);
    }
    const resolvedStops =
      (Array.isArray(payload.stopsResolved)
        ? payload.stopsResolved.filter((s) => (s || "").trim()).length
        : 0) > 0;
    return Boolean(payload.pickupResolved) && resolvedStops;
  }, [payload.roundTrip, payload.pickupResolved, payload.finalResolved, payload.stopsResolved]);

  useEffect(() => {
    if (!hasEnoughResolvedPoints) {
      if (payload.totalDistance != null || payload.durationHours != null) {
        clearEstimates();
      }
    }
  }, [
    hasEnoughResolvedPoints,
    routeSignature,
    payload.totalDistance,
    payload.durationHours,
    clearEstimates,
  ]);

const computeEstimateNow = useCallback(async () => {
  if (!hasEnoughResolvedPoints) return null;
  setLoadingDist(true);
  try {
    const est = await estimateTrip(payload);
    if (!est) return null;

    const patch = {
      totalDistance:
        typeof est.forwardDistanceKm === "number"
          ? Number(est.forwardDistanceKm.toFixed(1))
          : null,
      durationHours:
        typeof est.durationHours === "number"
          ? Number(est.durationHours.toFixed(2))
          : null,
      minDropOffsetMin:
        typeof est.minDropOffsetMin === "number" ? est.minDropOffsetMin : null,

      // ✅ store coords from backend
      pickupLat: typeof est.pickupLat === "number" ? est.pickupLat : null,
      pickupLng: typeof est.pickupLng === "number" ? est.pickupLng : null,
      dropLat: typeof est.dropLat === "number" ? est.dropLat : null,
      dropLng: typeof est.dropLng === "number" ? est.dropLng : null,
    };

    setPayload((p) => injectSelections({ ...p, ...patch }));
    lastComputedSigRef.current = routeSignature;

    return patch;
  } finally {
    setLoadingDist(false);
  }
}, [hasEnoughResolvedPoints, injectSelections, payload, routeSignature, setPayload]);


  useEffect(() => {
    if (!hasEnoughResolvedPoints) return;
    if (lastComputedSigRef.current === routeSignature) return;

    if (debTimerRef.current) clearTimeout(debTimerRef.current);
    debTimerRef.current = setTimeout(async () => {
      if (inflightRef.current) {
        try {
          await inflightRef.current;
        } catch {}
      }
      const p = computeEstimateNow();
      inflightRef.current = p;
      try {
        await p;
      } finally {
        inflightRef.current = null;
      }
    }, 350);

    return () => {
      if (debTimerRef.current) clearTimeout(debTimerRef.current);
    };
  }, [computeEstimateNow, hasEnoughResolvedPoints, routeSignature]);

const handleShowVehicles = async () => {
  let patch = null;

  if (hasEnoughResolvedPoints) {
    if (inflightRef.current) {
      try {
        patch = await inflightRef.current;
      } catch {}
    }

    const freshNeededAfterInflight =
      lastComputedSigRef.current !== routeSignature ||
      (patch == null && payload.totalDistance == null) ||
      (patch == null && payload.durationHours == null);

    if (freshNeededAfterInflight) {
      const nextEstimate = computeEstimateNow();
      inflightRef.current = nextEstimate;
      try {
        patch = await nextEstimate;
      } finally {
        inflightRef.current = null;
      }
    }
  }

  // ✅ ensure results page sees values immediately
  pushToResults(patch ? { ...payload, ...patch } : payload);
};

  /* ---------------- validation ---------------- */
  const hasPickup = !!payload.pickupLocation && !!payload.pickupResolved;
  const hasFinal = payload.roundTrip
    ? true
    : !!payload.finalDestination && !!payload.finalResolved;
  const hasPickupDT = !!payload.pickupDate && !!payload.pickupTime24;
  const hasDropDT = !!payload.releaseDate && !!payload.releaseTime24;
  const hasRadius = Number(payload.radius) > 0;

  const stopsCountTyped =
    payload.destinations?.filter((s) => (s || "").trim()).length || 0;
  const stopsCountResolved =
    payload.stopsResolved?.filter((s) => (s || "").trim()).length || 0;

  const stopRuleOk = payload.roundTrip ? stopsCountResolved >= 1 : true;

  const hasSelections = categories.length > 0 && types.length > 0;
  const scheduleLeadText = useMemo(() => {
    if (!scheduleConfigReady) return null;
    const safe = Math.max(0, Math.floor(Number(scheduleRequestThresholdMinutes) || 0));
    if (safe <= 0) return "Trips can be scheduled from now.";
    return `Trips can be scheduled at least ${formatLeadWindow(safe)} in advance.`;
  }, [scheduleConfigReady, scheduleRequestThresholdMinutes]);
  const hasCore =
    hasPickup && hasFinal && hasPickupDT && hasDropDT && hasRadius && stopRuleOk;
  const canSearch = hasCore && hasSelections;
  const searchButtonTitle = !canSearch
    ? !hasSelections
      ? "Select at least one category and one type"
      : payload.roundTrip && stopsCountResolved < 1
      ? "Add at least one confirmed stop for round trip"
      : "Fill/confirm all required fields"
    : loadingDist
    ? "Finishing estimate…"
    : "Search vehicles";

  const fromIso =
    payload.pickupDate && payload.pickupTime24
      ? `${payload.pickupDate}T${payload.pickupTime24}:00`
      : null;
  const toIso =
    payload.releaseDate && payload.releaseTime24
      ? `${payload.releaseDate}T${payload.releaseTime24}:00`
      : null;
  const timeOrderBad = useMemo(
    () => (fromIso && toIso ? new Date(fromIso) > new Date(toIso) : false),
    [fromIso, toIso]
  );
  const mobileStopValues =
    mobileMode
      ? payload.roundTrip
        ? (payload.destinations && payload.destinations.length > 0
            ? payload.destinations
            : [""])
        : payload.destinations || []
      : payload.destinations || [];

  return (
    <div className={mobileMode ? "pb-32" : "grid grid-cols-1 gap-8 xl:grid-cols-3"}>
      <div className={mobileMode ? "space-y-5" : "xl:col-span-2 space-y-5"}>
        {scheduleLeadText ? (
          <div
            className={`text-slate-500 ${
              mobileMode ? "flex items-center gap-1.5 px-0 pb-0 text-[12px]" : "px-0 pb-1 text-sm"
            }`}
          >
            {mobileMode ? <Icon path={mdiInformationOutline} size={0.6} className="opacity-70" /> : null}
            <span>{scheduleLeadText}</span>
          </div>
        ) : null}
        <Section
          title={mobileMode ? "" : "Trip type"}
          hint="Choose One-way or Round trip. Round trip returns to your pickup."
          mobileMode={mobileMode}
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-center md:gap-4">
            <div className="w-full">
              <div
                role="tablist"
                aria-label="Trip type"
                className={`grid w-full grid-cols-2 rounded-2xl border border-slate-200 bg-slate-50 ${
                  mobileMode ? "gap-1.5 p-1.5" : "gap-1 p-1"
                }`}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={!payload.roundTrip}
                  onClick={() => setTripType(false)}
                  className={[
                    mobileMode
                      ? "h-11 w-full rounded-xl text-center text-[15px] font-semibold transition"
                      : "w-full rounded-xl px-3 py-2 text-center text-sm font-semibold transition",
                    !payload.roundTrip
                      ? "bg-white text-slate-900 shadow border border-slate-200"
                      : "text-slate-600 hover:text-slate-900",
                  ].join(" ")}
                  title="One-way (pickup → destination)"
                >
                  One-way
                </button>

                <button
                  type="button"
                  role="tab"
                  aria-selected={payload.roundTrip}
                  onClick={() => setTripType(true)}
                  className={[
                    mobileMode
                      ? "inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-xl text-[15px] font-semibold transition"
                      : "inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition",
                    payload.roundTrip
                      ? "bg-white text-slate-900 shadow border border-slate-200"
                      : "text-slate-600 hover:text-slate-900",
                  ].join(" ")}
                  title="Round trip (return to pickup)"
                >
                  <Icon path={mdiSync} size={0.85} />
                  Round trip
                </button>
              </div>
            </div>

            {!mobileMode || hasEnoughResolvedPoints || loadingDist || payload.totalDistance != null ? (
              <div className={`inline-flex items-center gap-2 text-slate-600 ${mobileMode ? "text-xs" : "text-sm md:justify-end"}`}>
                <Icon path={mdiClockOutline} size={0.9} className="opacity-70" />
                {!hasEnoughResolvedPoints
                  ? "Select locations from suggestions to estimate distance"
                  : loadingDist
                  ? "Estimating…"
                  : payload.totalDistance != null
                  ? `~ ${Number(payload.totalDistance).toFixed(1)} km`
                  : "Ready to estimate"}
              </div>
            ) : null}
          </div>
        </Section>

        <Section
          title={mobileMode ? "" : "Trip details"}
          hint="Add pickup/drop and (optional) stops."
          mobileMode={mobileMode}
        >
          <div className={`grid grid-cols-1 gap-4 ${mobileMode ? "" : "md:grid-cols-2"}`}>
            <LocationAutoInput
              id="pickup"
              label={mobileMode ? undefined : "Pickup location *"}
              value={payload.pickupLocation}
              onChange={setPickupTyped}
              onSelect={setPickupSelected}
              placeholder={mobileMode ? "Pickup location" : "Start typing (e.g., Juhu…)"}
              required
              leading={
                mobileMode ? <Icon path={mdiMapMarkerOutline} size={0.9} className="text-slate-500" /> : null
              }
              trailing={
                mobileMode ? (
                  <button
                    type="button"
                    onClick={() => applyCurrentLocationToField("pickup")}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100"
                    aria-label="Use current location for pickup"
                  >
                    <Icon
                      path={mdiCrosshairsGps}
                      size={0.82}
                      className={locatingField === "pickup" ? "animate-pulse text-sky-600" : ""}
                    />
                  </button>
                ) : null
              }
              inputClassName={
                mobileMode
                  ? "mt-0 rounded-[14px] border-[1.5px] border-slate-200 py-4 pl-11 pr-12 text-[15px] placeholder:text-slate-500"
                  : ""
              }
            />

            {!payload.roundTrip && (
              <LocationAutoInput
                id="destination"
                label={mobileMode ? undefined : "Final destination *"}
                value={payload.finalDestination}
                onChange={setFinalTyped}
                onSelect={setFinalSelected}
                placeholder={mobileMode ? "Destination" : "Where to?"}
                required
                leading={
                  mobileMode ? <Icon path={mdiFlagOutline} size={0.9} className="text-slate-500" /> : null
                }
                trailing={
                  mobileMode ? (
                    <button
                      type="button"
                      onClick={() => applyCurrentLocationToField("destination")}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100"
                      aria-label="Use current location for destination"
                    >
                      <Icon
                        path={mdiCrosshairsGps}
                        size={0.82}
                        className={locatingField === "destination" ? "animate-pulse text-sky-600" : ""}
                      />
                    </button>
                  ) : null
                }
                inputClassName={
                  mobileMode
                    ? "mt-0 rounded-[14px] border-[1.5px] border-slate-200 py-4 pl-11 pr-12 text-[15px] placeholder:text-slate-500"
                    : ""
                }
              />
            )}
          </div>

          <div className="mt-4">
            {mobileMode ? (
              <div className="space-y-3">
                {mobileStopValues.map((val, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="flex-1">
                      <LocationAutoInput
                        id={`stop-${idx}`}
                        value={val}
                        onChange={(v) => updateStopTyped(idx, v)}
                        onSelect={(v) => updateStopSelected(idx, v)}
                        placeholder={`Stop ${idx + 1}`}
                        leading={
                          <Icon path={mdiMapMarkerPlus} size={0.9} className="text-slate-500" />
                        }
                        trailing={
                          <button
                            type="button"
                            onClick={() => applyCurrentLocationToField("stop", idx)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100"
                            aria-label={`Use current location for stop ${idx + 1}`}
                          >
                            <Icon
                              path={mdiCrosshairsGps}
                              size={0.82}
                              className={locatingField === `stop-${idx}` ? "animate-pulse text-sky-600" : ""}
                            />
                          </button>
                        }
                        inputClassName="mt-0 rounded-[14px] border-[1.5px] border-slate-200 py-4 pl-11 pr-12 text-[15px] placeholder:text-slate-500"
                      />
                    </div>
                    {payload.destinations?.[idx] != null ? (
                      <button
                        type="button"
                        onClick={() => removeStop(idx)}
                        className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-[14px] border border-slate-300 text-slate-600 hover:bg-slate-50"
                        aria-label={`Remove stop ${idx + 1}`}
                        title="Remove"
                      >
                        <Icon path={mdiTrashCanOutline} size={0.9} />
                      </button>
                    ) : null}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addStop}
                  className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-300 px-4 text-[14px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Icon path={mdiPlus} size={0.8} />
                  Add a stop
                </button>
                {payload.roundTrip && stopsCountResolved < 1 && (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                    Round trip requires at least one confirmed stop.
                  </div>
                )}
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setShowStops((s) => !s)}
                  className="inline-flex w-full items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm"
                >
                  <span className="inline-flex items-center gap-2">
                    <span>Stops</span>
                    {!!stopsCountTyped && (
                      <span className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-[8px] bg-slate-200 px-[6px] text-[11px] font-extrabold text-slate-700">
                        {stopsCountTyped}
                      </span>
                    )}
                  </span>
                  <Icon path={showStops ? mdiChevronUp : mdiChevronDown} size={0.9} />
                </button>

                {showStops && (
                  <div className="mt-2 space-y-2">
                    {payload.destinations?.map((val, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2">
                        <div className="col-span-11">
                          <LocationAutoInput
                            id={`stop-${idx}`}
                            value={val}
                            onChange={(v) => updateStopTyped(idx, v)}
                            onSelect={(v) => updateStopSelected(idx, v)}
                            placeholder={`Stop ${idx + 1}`}
                          />
                          {(payload.stopsResolved?.[idx] || "") === "" &&
                          (val || "").trim() ? (
                            <div className="mt-1 text-[11px] text-slate-500">
                              Pick from suggestions to confirm this stop
                            </div>
                          ) : null}
                        </div>
                        <div className="col-span-1 flex items-end">
                          <button
                            type="button"
                            onClick={() => removeStop(idx)}
                            className="h-10 w-10 inline-flex items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-50"
                            aria-label={`Remove stop ${idx + 1}`}
                            title="Remove"
                          >
                            <Icon path={mdiTrashCanOutline} size={0.9} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addStop}
                      className="mt-1 inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
                    >
                      <Icon path={mdiMapMarkerPlus} size={0.85} />
                      Add a stop
                    </button>
                    {payload.roundTrip && stopsCountResolved < 1 && (
                      <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                        Round trip requires at least one confirmed stop.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </Section>

        <Section
          title="Schedule"
          hint="Start and end time are required."
          mobileMode={mobileMode}
        >
          <DateTimeBlock
            payload={payload}
            setPayload={setPayload}
            injectSelections={injectSelections}
            routeKeySig={dropTimingKeySig}
            scheduleRequestThresholdMinutes={scheduleRequestThresholdMinutes}
            scheduleConfigReady={scheduleConfigReady}
            mobileMode={mobileMode}
          />
          {timeOrderBad && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              Drop time appears to be earlier than pickup time. Please review.
            </div>
          )}
        </Section>

        <Section
          title="Search radius"
          hint="We’ll look for vehicles within this distance."
          mobileMode={mobileMode}
        >
          {mobileMode ? (
            <div>
              <div className="flex h-14 items-center gap-3 rounded-[14px] border-[1.5px] border-slate-200 bg-white px-3">
                <div className="flex h-8 w-8 items-center justify-center text-slate-700">
                  <Icon path={mdiTapeMeasure} size={0.9} />
                </div>
                <input
                  type="number"
                  min={1}
                  max={200}
                  className="h-full flex-1 bg-transparent text-[15px] text-slate-900 outline-none placeholder:text-slate-500"
                  placeholder="Search radius (km)"
                  value={payload.radius}
                  onChange={(e) =>
                    setPayload((p) =>
                      injectSelections({
                        ...p,
                        radius: Number(e.target.value) || 20,
                      })
                    )
                  }
                  required
                />
                <div className="flex min-h-[28px] items-center justify-center rounded-[8px] border border-slate-200 bg-slate-100 px-2.5">
                  <span className="text-[12px] font-extrabold text-slate-700">km</span>
                </div>
              </div>
              <p className="mt-1.5 text-[12px] text-slate-600">
                We'll look for cars within this distance from your pickup.
              </p>
            </div>
          ) : (
            <div className="max-w-xs">
              <label className="text-sm font-medium">Radius (km) *</label>
              <input
                type="number"
                min={1}
                max={200}
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-300"
                value={payload.radius}
                onChange={(e) =>
                  setPayload((p) =>
                    injectSelections({
                      ...p,
                      radius: Number(e.target.value) || 20,
                    })
                  )
                }
                required
              />
            </div>
          )}
        </Section>

        {!mobileMode && <div className="flex justify-end pt-2">
          <motion.button
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
            className={`rounded-2xl px-6 py-3 text-white text-sm font-semibold ${
              canSearch && !loadingDist
                ? "bg-black"
                : "bg-slate-400 cursor-not-allowed"
            }`}
            onClick={handleShowVehicles}
            disabled={!canSearch || loadingDist}
            title={searchButtonTitle}
          >
            Show available vehicles
          </motion.button>
        </div>}
      </div>

      {!mobileMode && (
        <SummaryCard
          payload={payload}
          categories={categories}
          types={types}
        />
      )}

      {mobileMode && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] pt-3 backdrop-blur">
          <div className="mx-auto flex max-w-3xl items-center gap-2">
            {mobileFooterActions}
            <motion.button
              whileTap={{ scale: 0.98 }}
              className={`min-w-0 flex-[1.8] inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white ${
                canSearch && !loadingDist
                  ? "bg-black"
                  : "bg-slate-400 cursor-not-allowed"
              }`}
              onClick={handleShowVehicles}
              disabled={!canSearch || loadingDist}
              title={searchButtonTitle}
            >
              <Icon path={mdiArrowRight} size={0.82} />
              Show cars
            </motion.button>
          </div>
        </div>
      )}

      {locationErrorMessage ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold text-slate-900">Current location</div>
            <p className="mt-2 text-sm leading-6 text-slate-600">{locationErrorMessage}</p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setLocationErrorMessage("")}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- Date/time block (12h popup + strict mins) ---------------- */
/**
 * ✅ Backend provides payload.minDropOffsetMin (rounded to 15m step).
 * This becomes the exact min drop DT (pickup + offset).
 * If missing, we fallback to old ceil(hours) rule.
 */
function DateTimeBlock({
  payload,
  setPayload,
  injectSelections,
  routeKeySig,
  scheduleRequestThresholdMinutes,
  scheduleConfigReady,
  mobileMode = false,
}) {
  const inputCls =
    `mt-1 w-full rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300 ${
      mobileMode ? "px-4 py-4 text-[15px]" : "px-3 py-2"
    }`;

  const todayYmd = toYmd(new Date());
  const userTouchedDropRef = useRef(false);
  const userTouchedDropRouteSigRef = useRef("");

  const markDropTouched = useCallback(() => {
    userTouchedDropRef.current = true;
    userTouchedDropRouteSigRef.current = routeKeySig || "";
  }, [routeKeySig]);

  const setDropDateAuto = useCallback((value) => {
    setPayload((p) =>
      injectSelections({
        ...p,
        releaseDate: value,
      })
    );
  }, [injectSelections, setPayload]);

  const setDropTimeAuto = useCallback((value) => {
    setPayload((p) =>
      injectSelections({
        ...p,
        releaseTime24: value,
      })
    );
  }, [injectSelections, setPayload]);

  const schedulePickupThresholdDateTime = useMemo(() => {
    const base = new Date();
    base.setMinutes(
      base.getMinutes() + Math.max(0, Number(scheduleRequestThresholdMinutes) || 0)
    );
    return ceilToStepDate(base, STEP_MIN);
  }, [scheduleRequestThresholdMinutes]);

  const pickupMinDate = useMemo(
    () => toYmd(schedulePickupThresholdDateTime),
    [schedulePickupThresholdDateTime]
  );

  const pickupMinTime24 = useMemo(() => {
    if (!payload.pickupDate) return null;
    if (payload.pickupDate === toYmd(schedulePickupThresholdDateTime)) {
      return fmtTime24(schedulePickupThresholdDateTime);
    }
    return payload.pickupDate === todayYmd
      ? roundUpToStep(new Date(), STEP_MIN)
      : null;
  }, [payload.pickupDate, schedulePickupThresholdDateTime, todayYmd]);

  const pickupDT = useMemo(
    () => toLocalDateTime(payload.pickupDate, payload.pickupTime24),
    [payload.pickupDate, payload.pickupTime24]
  );

  const fallbackOffsetMin = useMemo(() => {
    const v = Number(payload.durationHours);
    if (!Number.isFinite(v) || v <= 0) return null;
    // old behavior fallback
    return Math.max(1, Math.ceil(v)) * 60;
  }, [payload.durationHours]);

  const requiredOffsetMin = useMemo(() => {
    const m = Number(payload.minDropOffsetMin);
    if (Number.isFinite(m) && m > 0) return m;
    return fallbackOffsetMin;
  }, [payload.minDropOffsetMin, fallbackOffsetMin]);

  const minDropDT = useMemo(() => {
    if (pickupDT && requiredOffsetMin) {
      const base = new Date(pickupDT.getTime() + requiredOffsetMin * 60 * 1000);
      return ceilToStepDate(base, STEP_MIN);
    }
    return null;
  }, [pickupDT, requiredOffsetMin]);

  const dropMinDate = useMemo(() => {
    if (minDropDT) return toYmd(minDropDT);
    return payload.pickupDate || todayYmd;
  }, [minDropDT, payload.pickupDate, todayYmd]);

  const getMinDropTimeForDate = useCallback((ymd) => {
    if (!ymd) return null;
    let min = null;
    if (minDropDT && ymd === toYmd(minDropDT)) {
      min = fmtTime24(minDropDT);
    }
    if (payload.pickupDate && payload.pickupTime24 && ymd === payload.pickupDate) {
      min = !min || payload.pickupTime24 > min ? payload.pickupTime24 : min;
    }
    if (ymd === todayYmd) {
      const nowMin = roundUpToStep(new Date(), STEP_MIN);
      min = !min || nowMin > min ? nowMin : min;
    }
    return min;
  }, [
    minDropDT,
    payload.pickupDate,
    payload.pickupTime24,
    todayYmd,
  ]);

  const dropMinTime24 = useMemo(
    () => getMinDropTimeForDate(payload.releaseDate),
    [getMinDropTimeForDate, payload.releaseDate]
  );

  useEffect(() => {
    console.info("[schedule-trip][web] pickup block state", {
      scheduleConfigReady,
      scheduleRequestThresholdMinutes,
      todayYmd,
      pickupMinDate,
      pickupMinTime24,
      currentPickupDate: payload.pickupDate || null,
      currentPickupTime24: payload.pickupTime24 || null,
      currentDropDate: payload.releaseDate || null,
      currentDropTime24: payload.releaseTime24 || null,
      routeKeySig,
    });
  }, [
    payload.pickupDate,
    payload.pickupTime24,
    payload.releaseDate,
    payload.releaseTime24,
    pickupMinDate,
    pickupMinTime24,
    routeKeySig,
    scheduleConfigReady,
    scheduleRequestThresholdMinutes,
    todayYmd,
  ]);

  const userLockedDropForCurrentRoute =
    userTouchedDropRef.current &&
    userTouchedDropRouteSigRef.current === (routeKeySig || "");

  useEffect(() => {
    if (!payload.pickupDate) return;
    if (payload.pickupDate < pickupMinDate) {
      setPayload((p) =>
        injectSelections({
          ...p,
          pickupDate: pickupMinDate,
          ...(pickupMinTime24 ? { pickupTime24: pickupMinTime24 } : {}),
        })
      );
    }
  }, [
    injectSelections,
    payload.pickupDate,
    pickupMinDate,
    pickupMinTime24,
    setPayload,
  ]);

  useEffect(() => {
    if (payload.pickupDate && payload.pickupTime24 && pickupMinTime24) {
      if (payload.pickupTime24 < pickupMinTime24) {
        setPayload((p) =>
          injectSelections({
            ...p,
            pickupTime24: pickupMinTime24,
          })
        );
      }
    }
  }, [
    pickupMinTime24,
    payload.pickupDate,
    payload.pickupTime24,
    injectSelections,
    setPayload,
  ]);

  useEffect(() => {
    if (!pickupDT) return;

    const minYmd = dropMinDate;
    const minTimeAtMinYmd = getMinDropTimeForDate(minYmd) || "00:00";

    if (!payload.releaseDate) {
      setDropDateAuto(minYmd);
      setDropTimeAuto(minTimeAtMinYmd);
      return;
    }

    if (!userLockedDropForCurrentRoute) {
      if (
        payload.releaseDate !== minYmd ||
        (payload.releaseTime24 || "") < minTimeAtMinYmd
      ) {
        setDropDateAuto(minYmd);
        setDropTimeAuto(minTimeAtMinYmd);
      }
      return;
    }

    if (payload.releaseDate < minYmd) {
      setDropDateAuto(minYmd);
      setDropTimeAuto(minTimeAtMinYmd);
      return;
    }

    const minTimeForCurrent = getMinDropTimeForDate(payload.releaseDate);
    if (
      minTimeForCurrent &&
      (!payload.releaseTime24 || payload.releaseTime24 < minTimeForCurrent)
    ) {
      setDropTimeAuto(minTimeForCurrent);
    }
  }, [
    dropMinDate,
    getMinDropTimeForDate,
    minDropDT,
    payload.releaseDate,
    payload.releaseTime24,
    pickupDT,
    payload.pickupDate,
    payload.pickupTime24,
    setDropDateAuto,
    setDropTimeAuto,
    userLockedDropForCurrentRoute,
  ]);

  useEffect(() => {
    if (!payload.releaseDate) return;
    const minTimeForCurrent = getMinDropTimeForDate(payload.releaseDate);
    if (
      minTimeForCurrent &&
      (!payload.releaseTime24 || payload.releaseTime24 < minTimeForCurrent)
    ) {
      setDropTimeAuto(minTimeForCurrent);
    }
  }, [
    getMinDropTimeForDate,
    payload.releaseDate,
    payload.releaseTime24,
    setDropTimeAuto,
  ]);

  return (
    <div
      className={`grid gap-4 ${
        mobileMode
          ? "[grid-template-columns:minmax(0,1.08fr)_minmax(0,1fr)]"
          : "grid-cols-1 md:grid-cols-2"
      }`}
    >
      <div className="min-w-0">
        <label className={`${mobileMode ? "text-[13px]" : "text-sm"} font-medium`}>Pickup date *</label>
        <div className="relative">
          {mobileMode ? (
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3 text-slate-500">
              <Icon path={mdiCalendarMonthOutline} size={0.86} />
            </div>
          ) : null}
          <input
            type="date"
            min={pickupMinDate}
            className={`${inputCls} ${mobileMode ? "pl-11 pr-8 text-[14px]" : ""}`}
            value={payload.pickupDate}
            disabled={!scheduleConfigReady}
            onChange={(e) => {
              const rawValue = e.target.value || "";
              const nextPickupDate =
                rawValue && rawValue < pickupMinDate ? pickupMinDate : rawValue;
              if (rawValue && rawValue < pickupMinDate) {
                console.warn("[schedule-trip][web] blocked pickup date before minimum", {
                  selectedPickupDate: rawValue,
                  enforcedPickupMinDate: pickupMinDate,
                  scheduleRequestThresholdMinutes,
                });
              } else {
                console.info("[schedule-trip][web] pickup date change accepted", {
                  selectedPickupDate: rawValue || null,
                  enforcedPickupMinDate: pickupMinDate,
                  scheduleRequestThresholdMinutes,
                });
              }
              setPayload((p) =>
                injectSelections({
                  ...p,
                  pickupDate: nextPickupDate,
                })
              );
            }}
            required
          />
        </div>
      </div>

      <div className="min-w-0">
        <label className={`${mobileMode ? "text-[13px]" : "text-sm"} font-medium`}>Pickup time *</label>
        <TimePicker12
          valueHHmm={payload.pickupTime24 || ""}
          onChangeHHmm={(val) =>
            setPayload((p) =>
              injectSelections({
                ...p,
                pickupTime24: val || "",
              })
            )
          }
          minHHmm={pickupMinTime24}
          disabled={!scheduleConfigReady || !payload.pickupDate}
          mobileMode={mobileMode}
          blockedLabel="Select time"
          onBlockedAttempt={
            !scheduleConfigReady
              ? null
              : () => window.alert("Select pickup date first.")
          }
          leadingIcon={
            mobileMode ? <Icon path={mdiClockOutline} size={0.86} /> : null
          }
        />
        {!scheduleConfigReady && (
          <div className="mt-1 text-[11px] text-slate-500">
            Loading allowed pickup window...
          </div>
        )}
      </div>

      <div className="min-w-0">
        <label className={`${mobileMode ? "text-[13px]" : "text-sm"} font-medium`}>Drop date *</label>
        <div className="relative">
          {mobileMode ? (
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3 text-slate-500">
              <Icon path={mdiCalendarMonthOutline} size={0.86} />
            </div>
          ) : null}
          <input
            type="date"
            min={dropMinDate}
            className={`${inputCls} ${mobileMode ? "pl-11 pr-8 text-[14px]" : ""}`}
            value={payload.releaseDate}
            onChange={(e) => {
              markDropTouched();
              setPayload((p) =>
                injectSelections({ ...p, releaseDate: e.target.value })
              );
            }}
            required
          />
        </div>
      </div>

      <div className="min-w-0">
        <label className={`${mobileMode ? "text-[13px]" : "text-sm"} font-medium`}>Drop time *</label>
        <TimePicker12
          valueHHmm={payload.releaseTime24 || ""}
          onChangeHHmm={(val) => {
            markDropTouched();
            setPayload((p) =>
              injectSelections({
                ...p,
                releaseTime24: val || "",
              })
            );
          }}
          minHHmm={dropMinTime24}
          disabled={!payload.releaseDate}
          mobileMode={mobileMode}
          blockedLabel="Select time"
          onBlockedAttempt={() => window.alert("Select drop date first.")}
          leadingIcon={
            mobileMode ? <Icon path={mdiClockOutline} size={0.86} /> : null
          }
        />
      </div>
    </div>
  );
}

/* ---------------- Summary card ---------------- */
function SummaryCard({ payload, categories, types }) {
  const actualHrs = useMemo(() => {
    if (
      !payload.pickupDate ||
      !payload.pickupTime24 ||
      !payload.releaseDate ||
      !payload.releaseTime24
    )
      return null;
    const start = toLocalDateTime(payload.pickupDate, payload.pickupTime24);
    const end = toLocalDateTime(payload.releaseDate, payload.releaseTime24);
    if (!start || !end) return null;
    const diffH = (end.getTime() - start.getTime()) / 3600000;
    if (Number.isNaN(diffH)) return null;
    return Math.max(0, Math.round(diffH * 100) / 100);
  }, [
    payload.pickupDate,
    payload.pickupTime24,
    payload.releaseDate,
    payload.releaseTime24,
  ]);

  const requiredHrs = useMemo(() => {
    const v = Number(payload.durationHours);
    return Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : null;
  }, [payload.durationHours]);

  const requiredOffsetHrs = useMemo(() => {
    const m = Number(payload.minDropOffsetMin);
    if (Number.isFinite(m) && m > 0) return Math.round((m / 60) * 100) / 100;
    return null;
  }, [payload.minDropOffsetMin]);

  const effectiveHrs = useMemo(() => {
    if (actualHrs == null && requiredHrs == null) return null;
    const a = actualHrs ?? 0;
    const r = requiredOffsetHrs ?? requiredHrs ?? 0;
    return Math.round(Math.max(a, r) * 100) / 100;
  }, [actualHrs, requiredHrs, requiredOffsetHrs]);

  const hasPickup = !!payload.pickupResolved;
  const hasFinal = payload.roundTrip ? true : !!payload.finalResolved;
  const hasPickupDT = !!payload.pickupDate && !!payload.pickupTime24;
  const hasDropDT = !!payload.releaseDate && !!payload.releaseTime24;
  const hasRadius = Number(payload.radius) > 0;

  const checklist = [
    { ok: hasPickup, label: "Pickup confirmed" },
    {
      ok: hasFinal,
      label: payload.roundTrip
        ? "Round trip (return set)"
        : "Destination confirmed",
    },
    { ok: hasPickupDT, label: "Pickup date & time" },
    { ok: hasDropDT, label: "Drop date & time" },
    { ok: hasRadius, label: "Search radius" },
  ];
  const allGood = checklist.every((c) => c.ok);

  const prettyTime = (t24) => to12From24(t24);

  const routeStops = useMemo(() => {
    const stops = (payload.stopsResolved || []).filter((s) => (s || "").trim());
    if (payload.roundTrip) {
      return [
        payload.pickupResolved || payload.pickupLocation || "—",
        ...stops,
        payload.pickupResolved || "—",
      ];
    }
    return [
      payload.pickupResolved || payload.pickupLocation || "—",
      ...stops,
      payload.finalResolved || payload.finalDestination || "—",
    ];
  }, [
    payload.pickupResolved,
    payload.pickupLocation,
    payload.finalResolved,
    payload.finalDestination,
    payload.stopsResolved,
    payload.roundTrip,
  ]);

  return (
    <aside className="xl:sticky xl:top-20 h-fit">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
          <div>
            <div className="text-xs font-semibold tracking-wide text-slate-500">
              TRIP SUMMARY
            </div>
            <div className="mt-0.5 text-[13px] text-slate-600">
              {payload.roundTrip ? "Round trip" : "One-way"}
            </div>
          </div>
          <div
            className={`inline-flex items-center gap-1 rounded-xl px-2.5 py-1 text-xs font-semibold border ${
              allGood
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
          >
            <Icon
              path={allGood ? mdiCheckCircleOutline : mdiAlertCircleOutline}
              size={0.75}
            />
            {allGood ? "Ready" : "Incomplete"}
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-2 text-slate-600 text-sm mb-3">
            <Icon path={mdiRoutes} size={0.95} />
            Route
          </div>
          <ol className="relative ml-3">
            {routeStops.map((label, idx) => {
              const last = idx === routeStops.length - 1;
              return (
                <li
                  key={`${label}-${idx}`}
                  className="pl-5 relative pb-3 last:pb-0"
                >
                  {!last && (
                    <span className="absolute left-1.5 top-3 h-full w-[2px] bg-slate-200" />
                  )}
                  <span
                    className={`absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full ring-2 ring-white ${
                      idx === 0
                        ? "bg-emerald-500"
                        : last && !payload.roundTrip
                        ? "bg-fuchsia-500"
                        : last && payload.roundTrip
                        ? "bg-emerald-500"
                        : "bg-slate-400"
                    }`}
                  />
                  <div className="text-sm text-slate-800">
                    {idx === 0
                      ? "Pickup: "
                      : last && !payload.roundTrip
                      ? "Destination: "
                      : last && payload.roundTrip
                      ? "Return: "
                      : "Stop: "}
                    <span className="text-slate-700">{label || "—"}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <MetricTile
            icon={mdiTapeMeasure}
            label="Distance"
            value={
              payload.totalDistance != null
                ? `${Number(payload.totalDistance).toFixed(1)} km`
                : "—"
            }
          />
          <MetricTile
            icon={mdiClockOutline}
            label="Duration (effective)"
            value={effectiveHrs != null ? `${effectiveHrs.toFixed(2)} hrs` : "—"}
            sub={
              (actualHrs != null || requiredHrs != null) && (
                <span className="text-[11px] text-slate-500">
                  {actualHrs != null && `Actual ${actualHrs.toFixed(2)}h`}
                  {actualHrs != null && requiredHrs != null && " • "}
                  {requiredHrs != null && `Backend ${requiredHrs.toFixed(2)}h`}
                  {requiredOffsetHrs != null && (
                    <>{" • "}Min drop +{requiredOffsetHrs.toFixed(2)}h</>
                  )}
                </span>
              )
            }
          />
        </div>

        <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
            <Icon path={mdiClockOutline} size={0.95} />
            Schedule
          </div>
          <div className="text-[13px] text-slate-800">
            <div>
              <span className="text-slate-500">Pickup:</span>{" "}
              {payload.pickupDate || "—"} {prettyTime(payload.pickupTime24)}
            </div>
            <div className="mt-0.5">
              <span className="text-slate-500">Drop:</span>{" "}
              {payload.releaseDate || "—"} {prettyTime(payload.releaseTime24)}
            </div>
          </div>

          {(categories?.length || types?.length || payload.radius) && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {categories?.map((c) => (
                <Chip key={`cat-${c}`} label={c} />
              ))}
              {types?.map((t) => (
                <Chip key={`type-${t}`} label={t} />
              ))}
              {payload.radius ? (
                <Chip label={`${payload.radius} km radius`} tone="outline" />
              ) : null}
            </div>
          )}
        </div>

        {!allGood && (
          <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3">
            <div className="text-xs font-semibold tracking-wide text-amber-700 mb-2">
              TO COMPLETE
            </div>
            <ul className="space-y-1.5">
              {checklist.map((c) => (
                <li key={c.label} className="flex items-center gap-2 text-[13px]">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      c.ok ? "bg-emerald-500" : "bg-amber-500"
                    }`}
                  />
                  <span
                    className={`${
                      c.ok ? "text-slate-400 line-through" : "text-slate-700"
                    }`}
                  >
                    {c.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );
}

/* --- light UI atoms --- */
function MetricTile({ icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">
          {label}
        </div>
        <Icon path={icon} size={0.9} className="text-slate-500" />
      </div>
      <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
      {sub ? <div className="mt-0.5">{sub}</div> : null}
    </div>
  );
}
function Chip({ label, tone = "neutral" }) {
  const base = "inline-flex items-center rounded-xl px-2.5 py-1 text-[12px] border";
  const styles =
    tone === "outline"
      ? "border-slate-300 text-slate-700 bg-white"
      : "border-slate-200 text-slate-700 bg-white";
  return <span className={`${base} ${styles}`}>{label}</span>;
}
